import {Grid, Typography, Box, Switch, FormControlLabel, RadioGroup, Radio, TextField, Tooltip, IconButton} from "@mui/material";
import {useTranslation} from "react-i18next";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import {useSnackbar} from "notistack";
import SnackbarUtil from "@/utils/SnackbarUtil";
import {useCallback} from "react";

// 定义组件的 props 接口
interface AttendanceOvertimeRuleProps {
  overtimeEnabled: boolean;
  overimeType: 'daily' | 'weekly';
  t1Value: string;
  t2Value: string;
  weeklyAlertsEnabled: boolean;
  hoursRangeLowMin: string;
  hoursRangeLowMax: string;
  hoursRangeMediumMin: string;
  hoursRangeMediumMax: string;
  hoursRangeHighMin: string;
  hoursRangeHighMax: string;
  subscriptionFlag: boolean;
  onOvertimeToggle: () => void;
  onOvertimeTypeChange: (value: 'daily' | 'weekly') => void;
  onT1Change: (value: string) => void;
  onT2Change: (value: string) => void;
  onIncrementT1: () => void;
  onDecrementT1: () => void;
  onIncrementT2: () => void;
  onDecrementT2: () => void;
  onWeeklyAlertsToggle: () => void;
  onHoursRangeLowMinChange: (value: string) => void;
  onHoursRangeLowMaxChange: (value: string) => void;
  onHoursRangeMediumMinChange: (value: string) => void;
  onHoursRangeMediumMaxChange: (value: string) => void;
  onHoursRangeHighMinChange: (value: string) => void;
  onHoursRangeHighMaxChange: (value: string) => void;
}

const AttendanceOvertimeRule: React.FC<AttendanceOvertimeRuleProps> = ({
  overtimeEnabled,
  overimeType,
  t1Value,
  t2Value,
  weeklyAlertsEnabled,
  hoursRangeLowMin,
  hoursRangeLowMax,
  hoursRangeMediumMin,
  hoursRangeMediumMax,
  hoursRangeHighMin,
  hoursRangeHighMax,
  subscriptionFlag,
  onOvertimeToggle,
  onOvertimeTypeChange,
  onT1Change,
  onT2Change,
  onIncrementT1,
  onDecrementT1,
  onIncrementT2,
  onDecrementT2,
  onWeeklyAlertsToggle,
  onHoursRangeLowMinChange,
  onHoursRangeLowMaxChange,
  onHoursRangeMediumMinChange,
  onHoursRangeMediumMaxChange,
  onHoursRangeHighMinChange,
  onHoursRangeHighMaxChange,
}) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  // 验证并处理 T 值的通用函数
  // Parameters: roundedValue (the current field value rounded), originalValue (the original input), min, max, otherValue, otherName, setValue, selfName
  const validateTValue = useCallback((roundedValue: string, originalValue: string, min: number, max: number, otherValue: string, otherName: string, setValue: (value: string) => void, selfName: string) => {
    const numValue = parseFloat(roundedValue);
    const numOriginal = parseFloat(originalValue);
    const numOther = parseFloat(otherValue);
    if (isNaN(numValue)) return;
    
    let newValue = numValue;
    let needsUpdate = false;
    
    // Helper to round value if decimal part <= 0.33
    const roundIfDecimalNum = (num: number): number => {
      const decimalPart = num - Math.floor(num);
      if (decimalPart > 0 && decimalPart <= 0.33) {
        return Math.ceil(num);
      }
      return num;
    };
    
    // 先检查与另一个值的关系
    // Compare the original values for T1/T2 relationship
    if (!isNaN(numOther)) {
      if (selfName === 'T1' && numOriginal >= numOther) {
        // Calculate the new value based on original comparison, using floor of other value then subtract 1
        newValue = Math.floor(numOther) - 1;
        needsUpdate = true;
      }
      if (selfName === 'T2' && numOriginal <= numOther) {
        // Calculate the new value based on original comparison, using floor of other value then add 1
        newValue = Math.floor(numOther) + 1;
        needsUpdate = true;
      }
    }
    
    // 然后检查范围
    if (newValue < min) {
      newValue = min;
      needsUpdate = true;
    }
    if (newValue > max) {
      newValue = max;
      needsUpdate = true;
    }
    
    // 如果值需要更新，设置新值
    if (needsUpdate) {
      setValue(newValue.toString());
      enqueueSnackbar(t(selfName === 'T1' ? "common.attendance.T1 Weekly Overtime must be between 8 and 80 hours and less than T2" : "common.attendance.T2 Weekly Overtime must be between 8 and 80 hours and greater than T1"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
  }, [enqueueSnackbar, t]);

  // Helpers to round decimal parts
  const roundIfDecimal = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    const decimalPart = num - Math.floor(num);
    // Keep decimal values with <= 2 decimal places, truncate only if decimal part is very small (< 0.01)
    // This allows values like 40.4, 40.44 to be preserved
    if (decimalPart > 0 && decimalPart < 0.01) {
      // Very small decimal part, truncate to integer
      return Math.floor(num).toString();
    }
    // For other decimal values, return the original value
    return value;
  };

  // Hours Range 验证函数 - 失去焦点时验证
  const handleHoursRangeBlur = useCallback((rangeType: 'low' | 'medium', side: 'min' | 'max', currentValue: string, otherValue: string, onChangeCallback: (value: string) => void, boundaryCallback?: (value: string) => void, lowMinValue?: string) => {
    // Use parseFloat to preserve decimal values for comparison
    const numCurrent = parseFloat(currentValue) || 0;
    const numOther = parseFloat(otherValue) || 0;
    const numLowMin = lowMinValue ? parseFloat(lowMinValue) : 0;
    const numHighMin = parseFloat(hoursRangeHighMin) || 0;
    
    // Helper to truncate decimal part (convert to integer)
    const truncateDecimal = (num: number): number => {
      return Math.floor(num);
    };
    
    // 如果是 max 且值小于等于 min，自动调整为 min + 1，然后截断为整数
    if (side === 'max' && numCurrent <= numOther) {
      const newValue = truncateDecimal(numOther + 1).toString();
      onChangeCallback(newValue);
      enqueueSnackbar(t("common.attendance.Hours Range maximum must be greater than minimum"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
      if (boundaryCallback) {
        boundaryCallback(newValue);
      }
    } else if (side === 'max' && boundaryCallback) {
      boundaryCallback(currentValue);
    }
    
    // 如果是 min 且值大于等于 max，自动调整为 max - 1，然后截断为整数
    if (side === 'min' && numCurrent >= numOther) {
      const newValue = truncateDecimal(numOther - 1).toString();
      onChangeCallback(newValue);
      enqueueSnackbar(t("common.attendance.Hours Range minimum must be less than maximum"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
    
    // 如果是 MediumMin，还需要验证它大于 LowMin，然后截断为整数
    if (rangeType === 'medium' && side === 'min') {
      if (numCurrent <= numLowMin) {
        const newValue = truncateDecimal(numLowMin + 1).toString();
        onChangeCallback(newValue);
        enqueueSnackbar(t("common.attendance.Medium Alert minimum must be greater than Low Alert minimum"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
      }
    }
    
    // 如果是 LowMax，验证它小于 MediumMax（即 MediumMax = HighMin）
    if (rangeType === 'low' && side === 'max' && boundaryCallback) {
      const numMediumMax = parseFloat(hoursRangeMediumMax) || 0;
      if (numCurrent >= numMediumMax) {
        const newValue = truncateDecimal(numMediumMax - 1).toString();
        onChangeCallback(newValue);
        enqueueSnackbar(t("common.attendance.Medium Alert minimum must equal Low Alert maximum"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
        if (boundaryCallback) {
          boundaryCallback(newValue);
        }
      }
    }
  }, [enqueueSnackbar, t, hoursRangeHighMin]);

  // High Alert 验证函数 - 失去焦点时验证
  const handleHighAlertBlur = useCallback((currentValue: string, mediumMinValue: string, onChangeCallback: (value: string) => void) => {
    // Use parseFloat to preserve decimal values for comparison
    const numCurrent = parseFloat(currentValue) || 0;
    const numMediumMin = parseFloat(mediumMinValue) || 0;
    
    // Helper to truncate decimal part (convert to integer)
    const truncateDecimal = (num: number): number => {
      return Math.floor(num);
    };
    
    // HighMin 必须大于 MediumMin，然后截断为整数
    if (numCurrent <= numMediumMin) {
      const newValue = truncateDecimal(numMediumMin + 1).toString();
      onChangeCallback(newValue);
      enqueueSnackbar(t("common.attendance.High Alert must be greater than Medium Alert minimum"), {variant: 'warning', anchorOrigin: {vertical: "top", horizontal: "center"}});
    }
  }, [enqueueSnackbar, t]);

  // Helper function for T value validation with rounding
  const validateTValueWithRounding = (value: string, min: number, max: number, otherValue: string, otherName: string, setValue: (value: string) => void, selfName: string) => {
    // First, round the current value if decimal part <= 0.33
    const roundedValue = roundIfDecimal(value);
    
    // Run validation to check T1/T2 relationship
    // Pass both the rounded value (for the current field) and the original value (for comparison if needed)
    validateTValue(roundedValue, value, min, max, otherValue, otherName, setValue, selfName);
  };

  const handleT1Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow positive numbers with up to 2 decimal places (empty value not allowed)
    const value = event.target.value;
    if (/^\d+\.?\d{0,2}$/.test(value)) {
      onT1Change(value);
    }
  };

  const handleT2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow positive numbers with up to 2 decimal places (empty value not allowed)
    const value = event.target.value;
    if (/^\d+\.?\d{0,2}$/.test(value)) {
      onT2Change(value);
    }
  };

  const handleT1Blur = () => {
    validateTValueWithRounding(t1Value, 8, 80, t2Value, 'T2', onT1Change, 'T1');
  };

  const handleT2Blur = () => {
    validateTValueWithRounding(t2Value, 8, 80, t1Value, 'T1', onT2Change, 'T2');
  };

  const handleWeeklyRadioClick = () => {
    if (!subscriptionFlag) {
      SnackbarUtil.warning(t("common.Subscribe in App Tips"), {
        anchorOrigin: {vertical: 'top', horizontal: 'center'}
      });
      return;
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{backgroundColor: '#ffffff', padding: '10px'}}>
          <FormControlLabel
            sx={{
              alignItems: 'center',
              marginBottom: "24px",
              flexDirection: 'row-reverse'
            }}
            label={
              <Typography sx={{fontSize: "16px", fontWeight: "bold", color: "#3D3D3D", marginLeft:"12px"}}>
                {t("common.Overtime Rule")}
              </Typography>
            }
            control={
              <Switch
                checked={overtimeEnabled}
                onChange={onOvertimeToggle}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#2BA9DE',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#2BA9DE',
                  },
                }}
              />
            }
          />
          {overtimeEnabled && (
            <Box>
              <NGInputLabel
                label={t("common.Overtime Type")}
                required={true}
                sx={{
                  fontSize: "20px",
                  color: "#3D3D3D",
                }}
              />
              <RadioGroup
                row
                value={overimeType}
                onChange={(event) => onOvertimeTypeChange((event.target as HTMLInputElement).value as 'daily' | 'weekly')}
                sx={{flexDirection: 'row'}}
              >
                <FormControlLabel
                  value="daily"
                  control={
                    <Radio
                      sx={{
                        color: '#666666',
                        '&.Mui-checked': {
                          color: '#2BA9DE',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{fontSize: "16px", fontWeight: "normal", color: "#3D3D3D"}}>
                      {t("common.Daily Overtime")}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="weekly"
                  onClick={handleWeeklyRadioClick}
                  control={
                    <Radio
                      disabled={!subscriptionFlag}
                      sx={{
                        color: '#666666',
                        '&.Mui-checked': {
                          color: '#2BA9DE',
                        },
                        '&.Mui-disabled': {
                          color: '#999999',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{fontSize: "16px", fontWeight: "normal", color: "#3D3D3D", opacity: subscriptionFlag ? 1 : 0.6}}>
                      {t("common.Weekly Overtime")}
                    </Typography>
                  }
                />
              </RadioGroup>
              {overimeType === 'daily' && (
                <Box>
                  <Typography sx={{fontSize: "16px", fontWeight: "bold", color: "#3D3D3D", marginBottom: "16px", marginTop: "24px"}}>
                    {t("common.attendance.Daily Overtime")}
                  </Typography>
                  <Box sx={{backgroundColor: '#f5f5f5', borderRadius: '8px', padding: '24px', width: '50%'}}>
                    <Typography sx={{fontSize: "16px", fontWeight: "bold", color: "#3D3D3D"}}>
                      {t("common.attendance.Daily Overtime is enabled")}
                    </Typography>
                    <Typography sx={{fontSize: "14px", color: "#666666", marginTop: "12px", paddingLeft: "8px", position: "relative"}}>
                      <span style={{position: "absolute", left: "0", top: "50%", transform: "translateY(-50%)", width: "3px", height: "3px", backgroundColor: "#666666", borderRadius: "50%"}}></span>
                      {t("common.attendance.Normal Schedule: Only Hours worked between Check-Out Time and Check-Out End Time are counted as overtime")}
                    </Typography>
                    <Typography sx={{fontSize: "14px", color: "#666666", marginTop: "12px", paddingLeft: "8px", position: "relative"}}>
                      <span style={{position: "absolute", left: "0", top: "50%", transform: "translateY(-50%)", width: "3px", height: "3px", backgroundColor: "#666666", borderRadius: "50%"}}></span>
                      {t("common.attendance.Flexible Schedule: Hours exceeding the Max Working Hours are counted as overtime")}
                    </Typography>
                  </Box>
                </Box>
              )}
              {overimeType === 'weekly' && (
                <Box>
                  <Typography sx={{fontSize: "16px", fontWeight: "bold", color: "#3D3D3D", marginBottom: "16px", marginTop: "24px"}}>
                    {t("common.attendance.Weekly Overtime")}
                  </Typography>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                      <Typography sx={{fontSize: "14px", fontWeight: "normal", color: "#3D3D3D"}}>
                        {t("common.attendance.T1 Weekly Overtime")}
                        <span style={{color: "red", marginLeft: "2px"}}>*</span>
                      </Typography>
                      <Tooltip title={t("common.attendance.T1 Weekly Overtime Tooltip")} placement="top" arrow>
                        <IconButton sx={{color: "#666666"}}>
                          <i className="icon iconfont icon-ic_about" style={{fontSize: "16px"}}></i>
                        </IconButton>
                      </Tooltip>
                      <Typography sx={{fontSize: "14px", color: "#666666", ml: '16px', mr: '6px'}}>{t("common.attendance.After")}</Typography>
                      <Box sx={{display: 'flex', alignItems: 'center', width: '160px', height: '40px', border: '1px solid #ccc', overflow: 'hidden'}}>
                        <Box onClick={onDecrementT1} sx={{flex: '0 0 40px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #ccc', cursor: 'pointer', "&:hover": {backgroundColor: "#f5f5f5"}}}>
                          <span style={{fontSize: "20px", fontWeight: "bold", color: "#999999"}}>-</span>
                        </Box>
                        <TextField
                          value={t1Value}
                          onChange={handleT1Change}
                          onBlur={handleT1Blur}
                          size="small"
                          variant="standard"
                          InputProps={{disableUnderline: true}}
                          sx={{flex: '0 0 80px', height: '100%', "& .MuiInputBase-input": {textAlign: "center", fontSize: "15px", padding: "10px 0", color: "#666666"}}}
                        />
                        <Box onClick={onIncrementT1} sx={{flex: '0 0 40px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #ccc', cursor: 'pointer', "&:hover": {backgroundColor: "#f5f5f5"}}}>
                          <span style={{fontSize: "20px", fontWeight: "bold", color: "#999999"}}>+</span>
                        </Box>
                      </Box>
                      <Typography sx={{fontSize: "14px", color: "#666666", ml: '6px'}}>{t("common.attendance.Hours")}</Typography>
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                      <Typography sx={{fontSize: "14px", fontWeight: "normal", color: "#3D3D3D"}}>
                        {t("common.attendance.T2 Weekly Overtime")}
                        <span style={{color: "red", marginLeft: "2px"}}>*</span>
                      </Typography>
                      <Tooltip title={t("common.attendance.T2 Weekly Overtime Tooltip")} placement="top" arrow>
                        <IconButton sx={{color: "#666666"}}>
                          <i className="icon iconfont icon-ic_about" style={{fontSize: "16px"}}></i>
                        </IconButton>
                      </Tooltip>
                      <Typography sx={{fontSize: "14px", color: "#666666", ml: '16px', mr: '6px'}}>{t("common.attendance.After")}</Typography>
                      <Box sx={{display: 'flex', alignItems: 'center', width: '160px', height: '40px', border: '1px solid #ccc', overflow: 'hidden'}}>
                        <Box onClick={onDecrementT2} sx={{flex: '0 0 40px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #ccc', cursor: 'pointer', "&:hover": {backgroundColor: "#f5f5f5"}}}>
                          <span style={{fontSize: "20px", fontWeight: "bold", color: "#999999"}}>-</span>
                        </Box>
                        <TextField
                          value={t2Value}
                          onChange={handleT2Change}
                          onBlur={handleT2Blur}
                          size="small"
                          variant="standard"
                          InputProps={{disableUnderline: true}}
                          sx={{flex: '0 0 80px', height: '100%', "& .MuiInputBase-input": {textAlign: "center", fontSize: "15px", padding: "10px 0", color: "#666666"}}}
                        />
                        <Box onClick={onIncrementT2} sx={{flex: '0 0 40px', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #ccc', cursor: 'pointer', "&:hover": {backgroundColor: "#f5f5f5"}}}>
                          <span style={{fontSize: "20px", fontWeight: "bold", color: "#999999"}}>+</span>
                        </Box>
                      </Box>
                      <Typography sx={{fontSize: "14px", color: "#666666", ml: '6px'}}>{t("common.attendance.Hours")}</Typography>
                    </Box>
                    {/* 灰色背景的 box，包含 Weekly Overtime Alerts、说明文字和表头 */}
                    <Box sx={{backgroundColor: '#f5f5f5', borderRadius: '8px', padding: '24px', marginTop: '16px', width: '90%', marginLeft: '0'}}>
                      <FormControlLabel
                        sx={{
                          alignItems: 'center',
                          marginBottom: "3px",
                          flexDirection: 'row-reverse'
                        }}
                        label={
                          <Typography sx={{fontSize: "16px", fontWeight: "bold", color: "#3D3D3D", marginLeft:"12px"}}>
                            {t("common.attendance.Weekly Overtime Alerts")}
                          </Typography>
                        }
                        control={
                          <Switch
                            checked={weeklyAlertsEnabled}
                            onChange={onWeeklyAlertsToggle}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#2BA9DE',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#2BA9DE',
                              },
                            }}
                          />
                        }
                      />
                      <Typography sx={{fontSize: "14px", color: "#3D3D3D", marginBottom: '16px', textAlign: 'left', fontWeight: '500'}}>
                        {t("common.attendance.Configure weekly overtime alert levels to identify employee overtime status in reports and analysis.")}
                      </Typography>

                      {/* 表头行 - 使用固定宽度布局对齐三列 */}
                      <Box sx={{display: 'flex', alignItems: 'center', mt: '30px', width: '100%'}}>
                        <Box sx={{width: '150px', ml: '30px'}}>
                          <Typography sx={{fontSize: "14px", color: "#3D3D3D", whiteSpace: 'nowrap'}}>{t("common.attendance.Alert Level")}</Typography>
                        </Box>
                        <Box sx={{width: '400px', ml: '420px'}}>
                          <Typography sx={{fontSize: "14px", color: "#3D3D3D", whiteSpace: 'nowrap'}}>{t("common.attendance.Hours Range")}</Typography>
                        </Box>
                        <Box sx={{width: '150px', ml: '180px'}}>
                          <Typography sx={{fontSize: "14px", color: "#3D3D3D", whiteSpace: 'nowrap'}}>{t("common.attendance.Alert Color")}</Typography>
                        </Box>
                      </Box>

                      {/* 白色 box，包含表格内容 */}
                      <Box sx={{backgroundColor: '#ffffff', borderRadius: '8px', padding: '24px', opacity: weeklyAlertsEnabled ? 1 : 0.5, pointerEvents: weeklyAlertsEnabled ? 'auto' : 'none', width: '100%', boxSizing: 'border-box', marginTop: '16px'}}>
                        {/* 表格内容 */}
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                        {/* Row 1: Low Alert */}
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '10px', width: '10px'}}>
                            <Box sx={{width: '12px', height: '12px', backgroundColor: '#4CAF50', borderRadius: '50%', position: 'relative', opacity: 0.3}}></Box>
                            <Box sx={{width: '8px', height: '8px', backgroundColor: '#4CAF50', borderRadius: '50%', position: 'absolute'}}></Box>
                          </Box>
                          <Typography sx={{fontSize: "14px", color: "#4CAF50", ml: '12px', whiteSpace: 'nowrap', width: '120px'}}>{t("common.attendance.Low Alert")}</Typography>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '400px', ml: '370px'}}>
                            <TextField
                              value={hoursRangeLowMin}
                              onChange={(e) => {
                                // Only allow positive numbers with up to 2 decimal places (empty not allowed)
                                const value = e.target.value;
                                if (/^\d+\.?\d{0,2}$/.test(value)) {
                                  onHoursRangeLowMinChange(value);
                                }
                              }}
                              onBlur={() => handleHoursRangeBlur('low', 'min', hoursRangeLowMin, hoursRangeLowMax, onHoursRangeLowMinChange, onHoursRangeLowMaxChange)}
                              size="small"
                              variant="outlined"
                              sx={{width: '80px', "& .MuiInputBase-input": {textAlign: "center", fontSize: "14px", padding: "6px 0"}}}
                            />
                            <Typography sx={{fontSize: "14px", color: "#999999"}}>-</Typography>
                            <TextField
                              value={hoursRangeLowMax}
                              onChange={(e) => {
                                // Only allow positive numbers with up to 2 decimal places (empty not allowed)
                                const value = e.target.value;
                                if (/^\d+\.?\d{0,2}$/.test(value)) {
                                  onHoursRangeLowMaxChange(value);
                                }
                              }}
                              onBlur={() => handleHoursRangeBlur('low', 'max', hoursRangeLowMax, hoursRangeLowMin, onHoursRangeLowMaxChange, onHoursRangeMediumMinChange)}
                              size="small"
                              variant="outlined"
                              sx={{width: '80px', "& .MuiInputBase-input": {textAlign: "center", fontSize: "14px", padding: "6px 0"}}}
                            />
                            <Typography sx={{fontSize: "14px", color: "#666666"}}>{t("common.attendance.h")}</Typography>
                          </Box>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '150px', ml: '250px'}}>
                            <Box sx={{width: '20px', height: '20px', backgroundColor: '#4CAF50', borderRadius: '2px'}}></Box>
                            <Typography sx={{fontSize: "14px", color: "#666666", whiteSpace: 'nowrap'}}>{t("common.attendance.Green")}</Typography>
                          </Box>
                        </Box>
                        {/* Row 2: Medium Alert */}
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '10px', width: '10px'}}>
                            <Box sx={{width: '12px', height: '12px', backgroundColor: '#FF9800', borderRadius: '50%', position: 'relative', opacity: 0.3}}></Box>
                            <Box sx={{width: '8px', height: '8px', backgroundColor: '#FF9800', borderRadius: '50%', position: 'absolute'}}></Box>
                          </Box>
                          <Typography sx={{fontSize: "14px", color: "#FF9800", ml: '12px', whiteSpace: 'nowrap', width: '120px'}}>{t("common.attendance.Medium Alert")}</Typography>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '400px', ml: '370px'}}>
                            <TextField
                              value={hoursRangeMediumMin}
                              onChange={(e) => {
                                // Only allow positive numbers with up to 2 decimal places (empty not allowed)
                                const value = e.target.value;
                                if (/^\d+\.?\d{0,2}$/.test(value)) {
                                  onHoursRangeMediumMinChange(value);
                                }
                              }}
                              onBlur={() => handleHoursRangeBlur('medium', 'min', hoursRangeMediumMin, hoursRangeMediumMax, onHoursRangeMediumMinChange, onHoursRangeMediumMaxChange, hoursRangeLowMin)}
                              size="small"
                              variant="outlined"
                              sx={{width: '80px', "& .MuiInputBase-input": {textAlign: "center", fontSize: "14px", padding: "6px 0"}}}
                            />
                            <Typography sx={{fontSize: "14px", color: "#999999"}}>-</Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <TextField
                                value={hoursRangeMediumMax}
                                onChange={(e) => {
                                  // Only allow positive numbers with up to 2 decimal places (empty not allowed)
                                  const value = e.target.value;
                                  if (/^\d+\.?\d{0,2}$/.test(value)) {
                                    onHoursRangeMediumMaxChange(value);
                                  }
                                }}
                                onBlur={() => handleHoursRangeBlur('medium', 'max', hoursRangeMediumMax, hoursRangeMediumMin, onHoursRangeMediumMaxChange, onHoursRangeHighMinChange)}
                                size="small"
                                variant="outlined"
                                sx={{width: '80px', "& .MuiInputBase-input": {textAlign: "center", fontSize: "14px", padding: "6px 0"}}}
                              />
                              <Typography sx={{fontSize: "14px", color: "#666666"}}>{t("common.attendance.h")}</Typography>
                              <Tooltip title={t("common.attendance.Overtime Risk Tooltip")} placement="top" arrow>
                                <IconButton sx={{color: "#666666", "&:hover": {color: "#2BA9DE"}}}>
                                  <i className="icon iconfont icon-ic_about" style={{fontSize: "16px"}}></i>
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '150px', ml: '250px'}}>
                            <Box sx={{width: '20px', height: '20px', backgroundColor: '#FF9800', borderRadius: '2px'}}></Box>
                            <Typography sx={{fontSize: "14px", color: "#666666", whiteSpace: 'nowrap'}}>{t("common.attendance.Orange")}</Typography>
                          </Box>
                        </Box>
                        {/* Row 3: High Alert */}
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', ml: '10px', width: '10px'}}>
                            <Box sx={{width: '12px', height: '12px', backgroundColor: '#F44336', borderRadius: '50%', position: 'relative', opacity: 0.3}}></Box>
                            <Box sx={{width: '8px', height: '8px', backgroundColor: '#F44336', borderRadius: '50%', position: 'absolute'}}></Box>
                          </Box>
                          <Typography sx={{fontSize: "14px", color: "#F44336", ml: '12px', whiteSpace: 'nowrap', width: '120px'}}>{t("common.attendance.High Alert")}</Typography>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '400px', ml: '370px'}}>
                            <Typography sx={{fontSize: "14px", color: "#666666"}}>{'>'}</Typography>
                            <TextField
                              value={hoursRangeHighMin}
                              onChange={(e) => {
                                // Only allow positive numbers with up to 2 decimal places (empty not allowed)
                                const value = e.target.value;
                                if (/^\d+\.?\d{0,2}$/.test(value)) {
                                  onHoursRangeHighMinChange(value);
                                }
                              }}
                              onBlur={() => handleHighAlertBlur(hoursRangeHighMin, hoursRangeMediumMin, onHoursRangeHighMinChange)}
                              size="small"
                              variant="outlined"
                              sx={{width: '80px', "& .MuiInputBase-input": {textAlign: "center", fontSize: "14px", padding: "6px 0"}}}
                            />
                            <Typography sx={{fontSize: "14px", color: "#666666"}}>{t("common.attendance.h")}</Typography>
                          </Box>
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', width: '150px', ml: '250px'}}>
                            <Box sx={{width: '20px', height: '20px', backgroundColor: '#F44336', borderRadius: '2px'}}></Box>
                            <Typography sx={{fontSize: "14px", color: "#666666", whiteSpace: 'nowrap'}}>{t("common.attendance.Red")}</Typography>
                          </Box>
                        </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default AttendanceOvertimeRule;
